import React, { createContext, useState, useContext, useEffect } from 'react';
import {
  getSessionId,
  getStoredUser,
  clearSession,
  exchangeToken,
  verifySession,
  logout as authLogout,
  extractTokenFromUrl,
  extractRegionFromUrl,
  cleanUrlAfterTokenExtraction,
  hasProduct,
} from '../services/auth';

const AuthContext = createContext(null);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within AuthProvider');
  }
  return context;
};

// DEV MODE: Set to true to bypass authentication for testing
const DEV_MODE_BYPASS_AUTH = true;

export const AuthProvider = ({ children }) => {
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  /**
   * Initialize authentication on app load
   */
  useEffect(() => {
    const initAuth = async () => {
      setLoading(true);
      setError(null);

      // DEV MODE: Bypass auth for local testing
      if (DEV_MODE_BYPASS_AUTH && window.location.hostname === 'localhost') {
        console.log('🔓 DEV MODE: Auth bypassed for testing');
        setUser({
          id: 'dev-user-12345',
          subscriptions: { 'FINMC': ['WKO', 'SBI', 'OPE', 'INCV', 'INC', 'TWO', 'TWOV', 'WKOV', 'UIT', '3RT', 'AOP', 'AVP', 'DSC', 'DVF', 'GEX', 'GEXV'] }
        });
        setIsAuthenticated(true);
        setLoading(false);
        return;
      }

      // Step 1: Check if token is in URL (redirect from OneClick Trading)
      const urlToken = extractTokenFromUrl(window.location.search);

      if (urlToken) {
        console.log('Token found in URL, exchanging for session...');
        const region = extractRegionFromUrl(window.location.search);

        // IMMEDIATELY clean URL to remove sensitive token from browser bar
        // Do this before async exchange so token is never visible for long
        cleanUrlAfterTokenExtraction();

        // Exchange token for session
        const result = await exchangeToken(urlToken, region);

        if (result.success) {
          setUser({
            id: result.session.user_id,
            subscriptions: result.session.subscriptions,
          });
          setIsAuthenticated(true);
          console.log('Session created successfully');
        } else {
          console.error('Token exchange failed:', result.error);
          setError(result.error);
          setIsAuthenticated(false);
          clearSession();
        }

        setLoading(false);
        return;
      }

      // Step 2: Check if session exists
      const sessionId = getSessionId();

      if (sessionId) {
        console.log('Session found, verifying...');

        // Verify session with backend
        const result = await verifySession();

        if (result.valid) {
          setUser(result.user);
          setIsAuthenticated(true);
          console.log('Session verified successfully');
        } else {
          console.error('Session invalid:', result.error);
          setError(result.error);
          setIsAuthenticated(false);
          clearSession();
        }
      } else {
        // No session found - check for stored user (development mode)
        const storedUser = getStoredUser();
        if (storedUser) {
          // Try to verify with backend
          const result = await verifySession();
          if (result.valid) {
            setUser(result.user);
            setIsAuthenticated(true);
          } else {
            console.log('No valid session found');
            setIsAuthenticated(false);
            setError('No authentication session found');
          }
        } else {
          console.log('No session or stored user found');
          setIsAuthenticated(false);
          setError('No authentication session found');
        }
      }

      setLoading(false);
    };

    initAuth();
  }, []);

  /**
   * Logout user
   */
  const logout = async () => {
    await authLogout();
    setUser(null);
    setIsAuthenticated(false);
    setError('Logged out');
  };

  /**
   * Refresh user data from session
   */
  const refreshUser = async () => {
    const result = await verifySession();
    if (result.valid) {
      setUser(result.user);
    } else {
      await logout();
    }
  };

  /**
   * Check if user has a specific product
   */
  const userHasProduct = (product) => {
    return hasProduct(user, product);
  };

  const value = {
    isAuthenticated,
    user,
    loading,
    error,
    logout,
    refreshUser,
    hasProduct: userHasProduct,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

/**
 * HOC to protect routes that require authentication
 */
export const withAuth = (Component) => {
  return function AuthenticatedComponent(props) {
    const { isAuthenticated, loading } = useAuth();

    if (loading) {
      return (
        <div className="flex items-center justify-center min-h-screen">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600" />
        </div>
      );
    }

    if (!isAuthenticated) {
      return (
        <div className="flex flex-col items-center justify-center min-h-screen">
          <h1 className="text-2xl font-bold text-gray-900 mb-4">
            Authentication Required
          </h1>
          <p className="text-gray-600">
            Please log in through the main portal to access this application.
          </p>
        </div>
      );
    }

    return <Component {...props} />;
  };
};
